import { DeleteOutlined } from '../../../components/icons'
import { Button } from '../../../components/ui/Button'

export interface StagingSelectionBandProps {
  /** Names the section this band governs (e.g. "identified parts"), used to
   * build each button's accessible name so two bands on one page are
   * distinguishable to a screen reader. Not rendered visibly — the visible
   * copy is the design's own, verbatim. */
  readonly sectionLabel: string
  readonly count: number
  /** Deselects every row in THIS section only. New — the design's `:3139`
   * affordance the build never had. */
  readonly onClear: () => void
  /** Removes every selected row in THIS section via the real
   * `removeStagedPart` path (see `StagingPage.deleteStagedIds`). */
  readonly onDeleteSelected: () => void
  readonly deleting?: boolean
  readonly deleteError?: string | undefined
  /**
   * ADR-0155 D6(b) — "a destructive bulk action must never act on rows the
   * user cannot see without the count saying so." `count` above is the
   * TRUE total selected in this section (visible + hidden-by-search) and is
   * already honest, but it is the ONLY signal on a band whose body may show
   * far fewer rows than `count` implies (a "select all" under an active
   * query used to add every hidden row too — the defect this prop exists to
   * disclose the aftermath of, now fixed at the call site). When > 0, this
   * many of `count`'s rows are currently hidden by the active search query;
   * rendering it here — additive to the design's otherwise-verbatim
   * three-item band — is a deliberate, security-driven exception to the "NO
   * additive content" rule below, not a redesign.
   *
   * WARNING D (Gate-5 re-review) — this is rendered as a plain, NON-live
   * `<span>`, deliberately. It used to be an `<output>` with no
   * `aria-live="off"` override, which kept `<output>`'s IMPLICIT polite
   * live-region role — the exact CRITICAL 3 defect (a region that
   * conditionally mounts on the 0→1 transition, so its first announcement
   * can be lost) reintroduced in this file while fixing it everywhere else.
   * With two flat sections both holding hidden selections, that was up to
   * two MORE polite regions firing alongside `StagingToolbar`'s one
   * consolidated region on a single keystroke. This fact is disclosed
   * VISUALLY only; if it should also be ANNOUNCED, that belongs in
   * `StagingPage.tsx`'s `searchDisclosureMessage` — the ONE region this
   * page owns — never as a second live region here.
   */
  readonly hiddenSelectedCount?: number
}

/**
 * PM ruling (2026-09-01) — the design's selection band
 * (`Fictiv Platform.dc.html:3136-3143`), re-read closely: it is NOT the
 * sticky action bar. It is a distinct contextual band welded directly under
 * a section header (`border-top:none`), tinted `--fictiv-teal-50` on
 * `--fictiv-teal-100`, containing EXACTLY three things —
 * `{{ selCount }} part(s) selected` · `Clear` · `Delete selected` — and NO
 * additive actions. "Confirm & add to library"/"Add to BOM" exist nowhere in
 * the design; the sticky selection/action card that used to hold them
 * (`StagingActionBar`) has been removed as not-in-design. The one primary
 * CTA lives in `StagingSummaryCard`.
 *
 * `Delete selected` takes the design's destructive styling VERBATIM
 * (`:3141`): white background, `var(--error)` text, `#F3C6C6` border, trash
 * icon — the shared `.btn-danger-outline` (`ui.css`) already matches this
 * exact treatment (white fill + error border/text), so no new CSS token is
 * invented for it.
 *
 * Renders nothing when `count` is 0 — a section with no selection shows no band.
 */
export function StagingSelectionBand({
  sectionLabel,
  count,
  onClear,
  onDeleteSelected,
  deleting = false,
  deleteError,
  hiddenSelectedCount = 0,
}: StagingSelectionBandProps) {
  if (count === 0) return null

  return (
    <div className="staging-selection-band">
      <span className="staging-selection-band-count">
        {count} part(s) selected
        {hiddenSelectedCount > 0 && (
          <span className="staging-selection-band-hidden-note">{` · ${hiddenSelectedCount} hidden by your search`}</span>
        )}
      </span>
      <Button
        variant="outline"
        size="sm"
        aria-label={`Clear ${sectionLabel} selection`}
        onClick={onClear}
      >
        Clear
      </Button>
      <Button
        variant="danger-outline"
        size="sm"
        className="staging-selection-band-delete"
        loading={deleting}
        aria-label={`Delete selected ${sectionLabel} (${count})`}
        onClick={onDeleteSelected}
      >
        <DeleteOutlined aria-hidden="true" /> Delete selected
      </Button>
      {deleteError && (
        <p role="alert" className="staging-row-inline-error">
          {deleteError}
        </p>
      )}
    </div>
  )
}
